import React, { useState } from 'react';

import { useLoggedInUser } from '@wac-web/utils/logged-in-user-provider';
import { FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import Heading from '@atlaskit/heading';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import { Profile } from '@atlaskit/navigation-system/top-nav-items';
import { Popup } from '@atlaskit/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';

import { BASE_URL, IDENTITY_BASE_URL } from '@/common/routes/constants';

import { messages } from './messages';

const baseStyles = xcss({
	marginInline: 'auto',
	width: '320px',
});

const avatarStyles = xcss({
	paddingInlineStart: 'space.200',
});

const LOGOUT_URL = `${IDENTITY_BASE_URL}/logout?continue=${encodeURIComponent(
	`${BASE_URL}/fastshift`,
)}`;

const onLogOutClick = () => {
	window.location.assign(LOGOUT_URL);
};

export const NavigationProfile: React.FC = () => {
	const [isOpen, setIsOpen] = useState(false);

	const intl = useIntl();
	const userInfo = useLoggedInUser();

	const onClose = () => {
		setIsOpen(false);
	};

	const renderTrigger = (triggerProps: TriggerProps) => (
		<Profile
			{...triggerProps}
			src={userInfo.picture}
			label="Your profile and settings"
			onClick={() => setIsOpen((prev) => !prev)}
			testId="profile-avatar-trigger"
		/>
	);

	const menu = () => (
		<Box xcss={baseStyles}>
			<MenuGroup testId="profile-content">
				<Section title={intl.formatMessage(messages.accountSectionTitle)}>
					<Inline xcss={avatarStyles} space="space.200" alignBlock="center">
						<Avatar size="medium" src={userInfo.picture} name={userInfo.name} />
						<Stack>
							<Heading size="small">{userInfo.name}</Heading>
							<Text>{userInfo.email}</Text>
						</Stack>
					</Inline>
				</Section>
				<Section hasSeparator>
					<ButtonItem onClick={onLogOutClick} testId="logout-button">
						<FormattedMessage {...messages.profileLogout} />
					</ButtonItem>
				</Section>
			</MenuGroup>
		</Box>
	);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={onClose}
			placement="bottom-end"
			zIndex={1000}
			content={menu}
			trigger={renderTrigger}
		/>
	);
};
